import { getVideos } from 'redux/middleware/api/media-upload-service/actions';
import { videoSelector } from 'redux/modules/entities/selectors';
import { ThunkAction } from 'redux/types';
import { IEmbedConfig, OneOrMore } from 'types';
import { asArray } from 'utils/collections';

type LoadVideosEmbedConfig = Pick<IEmbedConfig, 'videoClips'> | undefined;

type LoadVideosForConfigsOptions = {
  includeVariations?: boolean;
};

/**
 * Returns the unique video upload IDs referenced by the `videoClips` field of
 * each given embed config. Skips entries whose `videoId` is missing.
 */
export const getVideoClipIdsFromConfigs = (
  configs: OneOrMore<LoadVideosEmbedConfig>,
): number[] => {
  const ids = new Set<number>();

  for (const config of asArray(configs)) {
    for (const clip of config?.videoClips ?? []) {
      if (clip.videoId !== undefined) {
        ids.add(clip.videoId);
      }
    }
  }

  return Array.from(ids);
};

/**
 * Fetches the video upload entities referenced by the given embed configs
 * that aren't already present in the entity store.
 *
 * `includeVariations` controls whether the fetched entities carry their
 * variation metadata. The batch endpoint omits variations unless asked;
 * pass `true` from native-playback contexts that need to resolve a
 * transparent variation URL.
 *
 * Best-effort: does not throw on missing entities. Callers that need to
 * fail-on-missing (e.g. the advanced editor's strict load path) should layer
 * their own assertion on top.
 */
export const loadVideosForConfigs = (
  configs: OneOrMore<LoadVideosEmbedConfig>,
  { includeVariations = false }: LoadVideosForConfigsOptions = {},
): ThunkAction<Promise<void>> => async (dispatch, getState) => {
  const allIds = getVideoClipIdsFromConfigs(configs);
  const idsToFetch = allIds.filter(id => !videoSelector(getState(), id));

  if (idsToFetch.length === 0) {
    return;
  }

  await dispatch(getVideos({ ids: idsToFetch, includeVariations }));
};
